import React, { useEffect, useMemo } from 'react';
import { UiMessagesProvider } from '@packages/ui';
import type { UiMessages } from '@packages/ui';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import {
  ChangePasswordPage,
  CreditPackagesPage,
  CreditsPage,
  ForgotPasswordPage,
  HomePage,
  DashboardPage,
  LoginPage,
  NotificationsPage,
  PermissionsPage,
  ProfilePage,
  RecentPage,
  RegisterPage,
  TermsPage,
} from './pages';
import { Layout, ProtectedRoute } from './components/app-shell';
import { DialogProvider } from './hooks/useDialog';
import { useTheme } from './hooks/useTheme';
import { recordRecentVisit } from './utils/recentVisits';
import './i18n';
import './index.css';

const RecentVisitTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    recordRecentVisit(location.pathname);
  }, [location.pathname]);

  return null;
};

const RouterRoot: React.FC = () => (
  <>
    <RecentVisitTracker />
    <Outlet />
  </>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouterRoot />}>
      <Route index element={<HomePage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      <Route path="forgot-password" element={<ForgotPasswordPage />} />
      <Route path="packages" element={<CreditPackagesPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="permissions" element={<PermissionsPage />} />
        <Route path="recent" element={<RecentPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="credits" element={<CreditsPage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>,
  ),
);

const AppInner: React.FC = () => {
  useTheme();
  const { t, i18n } = useTranslation('common');

  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language;
  }, [i18n, i18n.language, i18n.resolvedLanguage]);

  const uiMessages = useMemo<UiMessages>(() => ({
    dialog: {
      confirm: t('ui.dialog.confirm'),
      cancel: t('ui.dialog.cancel'),
      closeAlert: t('ui.dialog.closeAlert'),
      closeConfirm: t('ui.dialog.closeConfirm'),
    },
    pagination: {
      label: t('ui.pagination.label'),
      previousPage: t('ui.pagination.previousPage'),
      nextPage: t('ui.pagination.nextPage'),
      pageLabel: (page) => t('ui.pagination.pageLabel', { page }),
      pageInfo: (start, end, total) => t('ui.pagination.pageInfo', { start, end, total }),
    },
  }), [t]);

  return (
    <UiMessagesProvider messages={uiMessages}>
      <DialogProvider>
        <RouterProvider router={router} />
      </DialogProvider>
    </UiMessagesProvider>
  );
};

const App: React.FC = () => <AppInner />;

export default App;
